import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, forkJoin, map, of } from 'rxjs';
import { SugarAccountSearchResult, SugarRecordReference } from 'shared-models';
import { SugarApiService } from 'src/app/services/sugar-api.service';
import { ConsumableService } from '../services/consumable.service';
import { MessageHandler } from '../shared/modals/services/message-handler.service';
import { ROUTES_NAME } from '../models/routes-name';
import { HttpErrorResponse } from '@angular/common/http';

export interface SugarContext {
	account: SugarAccountSearchResult;
	opportunity: SugarRecordReference;
}

/**
 * Verifica la validità delle entità Account e Opportunity di Sugar per la creazione di un preventivo.
 * Se uno dei due record non esiste o non è visibile, torna alla home con un toast
 */
export const sugarContextResolver: ResolveFn<SugarContext> = (route) => {
	const router = inject(Router);
	const accountId = route.queryParamMap.get('accountId');
	const opportunityId = route.queryParamMap.get('opportunityId');

	if (!accountId || !opportunityId) {
		return new RedirectCommand(router.parseUrl(ROUTES_NAME.HOME));
	}

	const sugarApiService = inject(SugarApiService);
	const consumableService = inject(ConsumableService);
	const messageHandler = inject(MessageHandler);

	const sugarAccount = sugarApiService.getAccount(accountId);
	const sugarOpportunity = sugarApiService.getOpportunity(opportunityId);

	/**
	 * Annulla la navigazione e torna alla home con un toast d'errore.
	 * Scarta anche l'eventuale offerta da duplicare: il configuratore non verrà creato e non la consumerebbe.
	 * @param messageKey
	 * @returns
	 */
	const backToHome = (messageKey: string) => {
		consumableService.clear();
		messageHandler.createErrorToast(messageKey);
		return new RedirectCommand(router.parseUrl(ROUTES_NAME.HOME));
	};

	return forkJoin([sugarAccount, sugarOpportunity]).pipe(
		map(([account, opportunity]) => {
			if (!account || !opportunity) {
				return backToHome('NEW_QUOTATION_PAGE.CONTEXT_ERROR_NOT_FOUND');
			}

			return {
				account,
				opportunity,
			};
		}),
		catchError((error) => {
			if (error instanceof HttpErrorResponse && error.status === 404) {
				return of(backToHome('NEW_QUOTATION_PAGE.CONTEXT_ERROR_NOT_FOUND'));
			}

			console.error('Lettura del contesto CRM fallita', error);
			return of(backToHome('NEW_QUOTATION_PAGE.CONTEXT_ERROR_UNREADABLE'));
		}),
	);
};
